import { useState, useMemo } from "react";
import { Box, Dialog, Divider, Drawer, InputAdornment, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { CaretCircleRight, CaretDown, CaretUp, Envelope, FileArrowUp, MagnifyingGlass, Phone, X } from "@phosphor-icons/react";

const contactOptions = [
  {
    icon: <Envelope size={20} weight="regular" color="#3F5EFB" />,
    title: "Par e-mail",
    subtitle: "Réponse sous 24h max",
    key: "email",
  },
  {
    icon: <Phone size={20} weight="regular" color="#3F5EFB" />,
    title: "Par téléphone",
    subtitle: "Rappel dans les 2h (ou choix horaire)",
    key: "phone",
  },
];

const categories = [
  {
    id: "rentabiliser",
    label: "Rentabiliser & performer avec The Gifts Club",
    faqs: [
      { q: "En combien de temps l'abonnement est rentable ?", a: "Dans la majorité des cas, quelques clients supplémentaires ou une seule campagne SMS suffisent à couvrir l'abonnement mensuel." },
      { q: "Combien de clients faut-il pour rentabiliser ?", a: "Souvent 3 à 10 clients par mois suffisent selon l'activité. Parfois une seule prestation ou quelques ventes couvrent le coût." },
      { q: "Pourquoi tu es rentable sans le voir ?", a: "Même quelques retours clients par mois peuvent rentabiliser la solution. Les avis, la base clients et les retours indirects ont une vraie valeur." },
      { q: "Un SMS peut payer ton mois ?", a: "Une campagne envoyée à ta base peut générer plusieurs retours immédiats. Un seul envoi bien ciblé peut couvrir l'abonnement." },
      { q: "Pourquoi les meilleurs clients restent chez The Gifts Club ?", a: "Ils accumulent avis, base clients et récurrence. Plus la solution tourne longtemps, plus elle devient rentable." },
      { q: "Avant de résilier, vérifie ça", a: "Vérifie la visibilité du QR, les gains, l'utilisation SMS et la configuration. Dans la majorité des cas, un simple ajustement suffit à relancer les résultats. (Ou appelle Lorenzo (+33 7 43 39 97 09) et Valentin (+337 83 37 26 53) si besoin)", highlight: true },
    ],
  },
  {
    id: "clients",
    label: "Générer plus de clients & avis",
    faqs: [
      { q: "Comment générer plus d'avis facilement ?", a: "Place le QR bien visible et mets l'action avis en priorité. Plus le parcours est simple, plus les avis augmentent." },
      { q: "Où placer le QR ?", a: "Caisse, comptoir, entrée, tables, packaging ou écran. Plus il est visible, plus les clients participent." },
      { q: "Combien de participations viser ?", a: "Une participation régulière chaque jour suffit à générer des résultats solides." },
      { q: "Comment améliorer rapidement les résultats ?", a: "Améliore la visibilité du QR, ajuste les gains et simplifie l'expérience client." },
      { q: "Erreurs fréquentes", a: "QR trop discret, gains peu motivants, manque de mise en avant ou lien mal configuré." },
      { q: "Quels gains fonctionnent le mieux ?", a: "Des gains simples, clairs et qui donnent envie de revenir consommer." },
      { q: "Comment faire revenir les clients ?", a: "Grâce aux gains à récupérer et aux campagnes SMS ciblées." },
    ],
  },
  {
    id: "sms",
    label: "SMS & base clients = chiffre d'affaires",
    faqs: [
      { q: "Pourquoi envoyer des SMS ?", a: "Le SMS est le canal le plus rentable, avec le meilleur taux d'ouverture pour faire revenir les clients rapidement." },
      { q: "Combien ça peut rapporter ?", a: "Même 5 à 10 retours clients après un SMS peuvent couvrir largement l'abonnement." },
      { q: "Quand envoyer ?", a: "Heures creuses, nouveautés, offres spéciales ou événements." },
      { q: "Comment remplir les heures creuses ?", a: "En envoyant un SMS ciblé avec une offre simple et claire." },
      { q: "Valeur d'une base clients", a: "Une base clients permet de relancer, fidéliser et augmenter la valeur du point de vente." },
      { q: "Pourquoi c'est un actif business ?", a: "Une base clients active augmente la valeur d'un commerce en cas de revente ou développement." },
      { q: "Préparer WhatsApp & email", a: "La base collectée permettra d'activer d'autres canaux comme WhatsApp et email marketing." },
    ],
  },
  {
    id: "campagne",
    label: "Configurer ma campagne",
    faqs: [
      { q: "Où configurer ma campagne ?", a: "Dans Ma campagne > Configuration." },
      { q: "Puis-je modifier la campagne ?", a: "Oui à tout moment : actions, gains, ordre, visuels." },
      { q: "Combien d'actions ?", a: "1 action = 1 participation actuellement. Le multi-actions arrive bientôt." },
      { q: "Où modifier le PIN ?", a: "Dans Ma campagne > Modifier le code PIN." },
      { q: "Le client récupère son gain quand ?", a: "Généralement lors d'une prochaine visite sous 24h à 2 semaines." },
      { q: "Peut-on personnaliser ?", a: "Oui : gains, visuels, actions et jeu." },
      { q: "Le QR change si je modifie ?", a: "Non, le QR reste identique." },
    ],
  },
  {
    id: "avis",
    label: "Avis & réputation",
    faqs: [
      { q: "Où mettre mon lien Google ?", a: "Dans Ma campagne > Action Avis Google." },
      { q: "Puis-je utiliser Tripadvisor ?", a: "Oui, comme action." },
      { q: "Pourquoi répondre aux avis ?", a: "Cela améliore la confiance et la visibilité de votre fiche google." },
      { q: "L'IA peut répondre ?", a: "Oui, automatiquement ou avec validation." },
      { q: "Comment gérer un avis négatif ?", a: "Répondre rapidement, rester professionnel et proposer une solution." },
      { q: "Les avis améliorent-ils Google ?", a: "Oui, ils améliorent la visibilité locale." },
    ],
  },
  {
    id: "problemes",
    label: "Problèmes fréquents",
    faqs: [
      { q: "Pas de résultats", a: "Vérifie visibilité QR, gains et configuration." },
      { q: "QR ne marche pas", a: "Tester avec un autre téléphone ou en 4G." },
      { q: "Pas d'avis", a: "Vérifier le lien Google et la participation." },
      { q: "SMS non reçus", a: "Vérifier numéros et blocages opérateurs." },
      { q: "Les clients ne participent pas", a: "QR trop discret ou gains peu motivants." },
      { q: "Le staff n'utilise pas", a: "Rendre le QR visible pour automatiser l'usage." },
    ],
  },
  {
    id: "compte",
    label: "Compte & support",
    faqs: [
      { q: "Qui peut accéder au compte ?", a: "Le gérant ou un responsable." },
      { q: "Modifier le code PIN", a: "Dans Ma campagne rubrique « code pin »." },
      { q: "Les données sont-elles sécurisées ?", a: "Oui, conformes RGPD." },
      { q: "Les données m'appartiennent ?", a: "Oui." },
      { q: "Multi établissements ?", a: "Disponible progressivement." },
      { q: "Besoin d'aide rapide ?", a: "Contacte-nous directement." },
      { q: "Support Lorenzo", a: "+33 7 43 39 97 09" },
      { q: "Support Valentin", a: "+33 7 83 37 26 53" },
    ],
  },
];

interface FaqItemProps {
  q: string;
  a: string;
  highlight?: boolean;
  defaultOpen?: boolean;
}

function FaqItem({ q, a, highlight, defaultOpen }: FaqItemProps) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  return (
    <Box
      onClick={() => setOpen((v) => !v)}
      sx={{
        borderRadius: "8px",
        border: open ? "1.5px solid #3F5EFB" : "1px solid transparent",
        bgcolor: open ? "#F0F3FF" : "#fff",
        cursor: "pointer",
        px: "32px",
        py: "22px",
        "&:hover": { bgcolor: open ? "#F0F3FF" : "#FAFAFA" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "20px" }}>
          <Box sx={{ width: 4, minHeight: 21, borderRadius: "2px", bgcolor: open ? "#3F5EFB" : "#C7D2FF", flexShrink: 0, mt: "2px" }} />
          <Typography sx={{ fontSize: "16px", fontWeight: 600, fontFamily: "Roboto, sans-serif", color: "#000", lineHeight: "normal" }}>
            {q}
          </Typography>
        </Box>
        {open ? <CaretUp size={20} color="#666" style={{ flexShrink: 0, marginTop: 2 }} /> : <CaretDown size={20} color="#666" style={{ flexShrink: 0, marginTop: 2 }} />}
      </Box>
      {open && (
        <Typography sx={{ fontSize: "16px", fontWeight: 400, fontFamily: "Roboto, sans-serif", color: "#000", mt: "12px", ml: "24px", lineHeight: "normal" }}>
          {a}
        </Typography>
      )}
    </Box>
  );
}

interface HelpCenterProps {
  open: boolean;
  onClose: () => void;
  onContact?: () => void;
}

export default function HelpCenter({ open, onClose, onContact }: HelpCenterProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [search, setSearch] = useState("");
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneTime, setPhoneTime] = useState("Dans 1 heure");
  const [phoneNote, setPhoneNote] = useState("");
  const [phoneSending, setPhoneSending] = useState(false);
  const [phoneSent, setPhoneSent] = useState(false);

  async function handleSendPhone() {
    if (!phoneNumber.trim()) return;
    setPhoneSending(true);
    try {
      await fetch("https://formsubmit.co/ajax/lorenzo.gritti@thegiftsclub.io", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: "Demande de rappel téléphonique - The Gifts Club",
          telephone: phoneNumber,
          rappel: phoneTime,
          note: phoneNote,
        }),
      });
      setPhoneSent(true);
      setPhoneNumber("");
      setPhoneNote("");
      setPhoneTime("Dans 1 heure");
    } finally {
      setPhoneSending(false);
    }
  }

  async function handleSendEmail() {
    if (!emailMessage.trim()) return;
    setEmailSending(true);
    try {
      await fetch("https://formsubmit.co/ajax/lorenzo.gritti@thegiftsclub.io", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ message: emailMessage, _subject: "Demande de support - The Gifts Club" }),
      });
      setEmailSent(true);
      setEmailMessage("");
    } finally {
      setEmailSending(false);
    }
  }

  const searchResults = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    return categories.flatMap((cat) =>
      cat.faqs
        .filter((faq) => faq.q.toLowerCase().includes(q) || faq.a.toLowerCase().includes(q))
        .map((faq) => ({ ...faq, catLabel: cat.label }))
    );
  }, [search]);

  const activeFaqs = searchResults ?? categories[selectedCategory].faqs;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: "#FAFAFA", overflowY: "auto" } }}
    >
      <Box sx={{ maxWidth: 1200, mx: "auto", width: "100%", px: isMobile ? 2 : "60px", py: isMobile ? 3 : "40px", minHeight: "100vh" }}>
        {/* Top bar */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative", mb: isMobile ? 3 : "60px" }}>
          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#999", fontFamily: "Roboto, sans-serif" }}>
            Aide
          </Typography>
          <Box
            onClick={onClose}
            sx={{ position: "absolute", right: 0, display: "flex", alignItems: "center", gap: 1, cursor: "pointer", "&:hover": { opacity: 0.7 } }}
          >
            <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#999", fontFamily: "Roboto, sans-serif" }}>
              Fermer
            </Typography>
            <X size={22} color="#999" />
          </Box>
        </Box>

        {/* Title */}
        <Typography sx={{ fontSize: isMobile ? "20px" : "24px", fontWeight: 700, fontFamily: "Montserrat, sans-serif", color: "#1C1C1C", textAlign: "center", mb: isMobile ? 3 : "40px" }}>
          On est là pour vous faire gagner du temps
        </Typography>

        {/* Search */}
        <TextField
          fullWidth
          placeholder={'Rechercher un problème… "Campagne SMS", "Avis", "Facturation"…'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MagnifyingGlass size={22} color="#999" />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: isMobile ? 1.5 : "12px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              bgcolor: "#fff",
              fontSize: "16px",
              fontFamily: "Roboto, sans-serif",
              "& fieldset": { borderColor: "#E1E1E2", borderWidth: "1.5px" },
              "&:hover fieldset": { borderColor: "#bbb" },
            },
          }}
        />

        {/* Keyword chips */}
        {!search && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: isMobile ? 3 : "32px" }}>
            {["Campagne SMS", "Avis", "Facturation"].map((kw) => (
              <Box
                key={kw}
                onClick={() => setSearch(kw)}
                sx={{
                  px: "14px",
                  py: "6px",
                  border: "1px solid #F0F0F0",
                  borderRadius: "10px",
                  bgcolor: "#fff",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: 500,
                  fontFamily: "Instrument Sans, sans-serif",
                  color: "#3F5EFB",
                  "&:hover": { bgcolor: "#F0F3FF" },
                }}
              >
                {kw}
              </Box>
            ))}
          </Box>
        )}

        {/* Categories section */}
        {!searchResults && (
          <>
            <Typography sx={{ fontSize: "18px", fontWeight: 700, fontFamily: "Montserrat, sans-serif", color: "#1C1C1C", mb: 2 }}>
              Trouvez votre réponse par catégorie
            </Typography>
            <Box sx={{ display: "flex", flexWrap: isMobile ? "nowrap" : "wrap", gap: "14px", mb: "28px", overflowX: isMobile ? "auto" : "visible", pb: isMobile ? "4px" : 0, "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none", ...(isMobile && { mx: -2, px: 2, pr: 2 }) }}>
              {categories.map((cat, i) => (
                <Box
                  key={cat.id}
                  onClick={() => setSelectedCategory(i)}
                  sx={{
                    px: "16px",
                    py: "12px",
                    borderRadius: "10px",
                    border: selectedCategory === i ? "2px solid #3F5EFB" : "1px solid #F0F0F0",
                    bgcolor: selectedCategory === i ? "#F0F3FF" : "#fff",
                    cursor: "pointer",
                    fontSize: "16px",
                    fontWeight: 500,
                    fontFamily: "Inter, sans-serif",
                    color: "#000",
                    textAlign: "center",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    "&:hover": { bgcolor: selectedCategory === i ? "#F0F3FF" : "#F8F8F8" },
                    transition: "all 0.15s",
                  }}
                >
                  {cat.label}
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* FAQ items */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mb: "60px" }}>
          {activeFaqs.map((faq, i) => (
            <FaqItem
              key={i}
              q={faq.q}
              a={faq.a}
              highlight={(faq as any).highlight}
              defaultOpen={(faq as any).highlight}
            />
          ))}
          {searchResults && searchResults.length === 0 && (
            <Typography sx={{ fontSize: "14px", color: "#999", fontFamily: "Roboto, sans-serif", textAlign: "center", py: 4 }}>
              Aucun résultat pour cette recherche.
            </Typography>
          )}
        </Box>

        {/* Footer */}
        <Divider sx={{ mb: "40px" }} />
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "20px", pb: "40px" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, fontFamily: "Montserrat, sans-serif", color: "#1C1C1C" }}>
            Vous avez toujours besoin d'aide ?
          </Typography>
          <Box
            onClick={() => setContactModalOpen(true)}
            sx={{
              px: "24px",
              py: "14px",
              border: "1px solid #F0F0F0",
              borderRadius: "10px",
              bgcolor: "#fff",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: 500,
              fontFamily: "Inter, sans-serif",
              color: "#3F5EFB",
              "&:hover": { bgcolor: "#F8F8F8" },
            }}
          >
            Contacter notre équipe
          </Box>
        </Box>
      </Box>

      {/* Contact modal */}
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          slotProps={{ paper: { sx: { borderRadius: "16px 16px 0 0", px: "16px", pt: "16px", pb: "32px" } } }}
          sx={{ zIndex: 1400 }}
        >
          <Box sx={{ width: 40, height: 4, bgcolor: "#E0E0E0", borderRadius: "2px", mx: "auto", mb: "20px" }} />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "16px" }}>
            <Box onClick={() => setContactModalOpen(false)} sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}>
              <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#999", fontFamily: "Roboto, sans-serif" }}>Fermer</Typography>
              <X size={22} color="#999" />
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {contactOptions.map((opt) => (
              <Box
                key={opt.title}
                onClick={() => { if (opt.key === "email") { setContactModalOpen(false); setEmailSent(false); setEmailOpen(true); } if (opt.key === "phone") { setContactModalOpen(false); setPhoneSent(false); setPhoneOpen(true); } }}
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "20px", borderRadius: "16px", border: "1px solid #F0F0F0", bgcolor: "#fff", cursor: "pointer", "&:hover": { bgcolor: "#FAFAFA" } }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "19px" }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: "20px", bgcolor: "#F0F3FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{opt.icon}</Box>
                  <Box>
                    <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#000", fontFamily: "Roboto, sans-serif" }}>{opt.title}</Typography>
                    <Typography sx={{ fontSize: "16px", fontWeight: 400, color: "#000", fontFamily: "Roboto, sans-serif" }}>{opt.subtitle}</Typography>
                  </Box>
                </Box>
                <CaretCircleRight size={20} color="#000" weight="regular" />
              </Box>
            ))}
          </Box>
        </Drawer>
      ) : (
      <Dialog
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            width: 496,
            maxWidth: "calc(100vw - 32px)",
            p: 0,
            overflow: "hidden",
            boxShadow: "0px 0px 30px 25px rgba(0,0,0,0.03)",
          },
        }}
      >
        <Box sx={{ pt: "28px", px: "25px", pb: "28px" }}>
          {/* Close */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "28px" }}>
            <Box
              onClick={() => setContactModalOpen(false)}
              sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", "&:hover": { opacity: 0.7 } }}
            >
              <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#999", fontFamily: "Roboto, sans-serif" }}>
                Fermer
              </Typography>
              <X size={22} color="#999" />
            </Box>
          </Box>

          {/* Options */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {contactOptions.map((opt) => (
              <Box
                key={opt.title}
                onClick={() => { if (opt.key === "email") { setContactModalOpen(false); setEmailSent(false); setEmailOpen(true); }
                if (opt.key === "phone") { setContactModalOpen(false); setPhoneSent(false); setPhoneOpen(true); } }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: "20px",
                  borderRadius: "16px",
                  border: "1px solid #F0F0F0",
                  bgcolor: "#fff",
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": { bgcolor: "#FAFAFA" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "19px" }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "20px",
                      bgcolor: "#F0F3FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: "0px 0px 24px rgba(0,0,0,0.03)",
                    }}
                  >
                    {opt.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#000", fontFamily: "Roboto, sans-serif", letterSpacing: "0.16px" }}>
                      {opt.title}
                    </Typography>
                    <Typography sx={{ fontSize: "16px", fontWeight: 400, color: "#000", fontFamily: "Roboto, sans-serif", letterSpacing: "0.16px" }}>
                      {opt.subtitle}
                    </Typography>
                  </Box>
                </Box>
                <CaretCircleRight size={20} color="#000" weight="regular" />
              </Box>
            ))}
          </Box>
        </Box>
      </Dialog>
      )}

      {/* Email contact fullscreen */}
      <Dialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        fullScreen
        PaperProps={{ sx: { bgcolor: "#FAFAFA", overflowY: "auto" } }}
      >
        <Box sx={{ maxWidth: 860, mx: "auto", width: "100%", px: isMobile ? 2 : "60px", py: isMobile ? 3 : "40px", minHeight: "100vh" }}>
          {/* Top bar */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative", mb: "60px" }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1C1C1C", fontFamily: "Roboto, sans-serif" }}>
              Contact e-mail
            </Typography>
            <Box
              onClick={() => setEmailOpen(false)}
              sx={{ position: "absolute", right: 0, display: "flex", alignItems: "center", gap: 1, cursor: "pointer", "&:hover": { opacity: 0.7 } }}
            >
              <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#999", fontFamily: "Roboto, sans-serif" }}>
                Fermer
              </Typography>
              <X size={22} color="#999" />
            </Box>
          </Box>

          {/* Title section */}
          <Box sx={{ display: "flex", gap: "25px", alignItems: "flex-start", mb: "28px" }}>
            <Box sx={{ width: 4, minHeight: 61, borderRadius: "2px", bgcolor: "#3F5EFB", flexShrink: 0 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Typography sx={{ fontSize: "24px", fontWeight: 700, fontFamily: "Roboto, sans-serif", color: "#1C1C1C" }}>
                Envoyez-nous votre demande
              </Typography>
              <Typography sx={{ fontSize: "16px", fontWeight: 400, fontFamily: "Roboto, sans-serif", color: "#000" }}>
                Nous vous répondons sous 24h maximum.
              </Typography>
            </Box>
          </Box>

          {emailSent ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
              <Box sx={{
                bgcolor: "#fff", border: "1.5px solid #E1E1E2", borderRadius: "8px",
                width: 510, maxWidth: "100%", p: "40px 54px",
                display: "flex", flexDirection: "column", alignItems: "center", gap: "20px",
              }}>
                <Box component="img" src="/assets/success.png" alt="" sx={{ width: 115, height: 134, objectFit: "contain" }} />
                <Typography sx={{ fontSize: "26px", fontWeight: 600, fontFamily: "Roboto, sans-serif", textAlign: "center", lineHeight: 1.3 }}>
                  Merci, votre message a bien été{" "}
                  <Box component="span" sx={{ color: "#3F5EFB" }}>envoyé !</Box>
                </Typography>
                <Typography sx={{ fontSize: "16px", fontFamily: "Roboto, sans-serif", color: "#999", textAlign: "center", letterSpacing: "0.16px" }}>
                  Nous vous répondons sous 24h maximum.
                </Typography>
                <Box
                  onClick={() => setEmailOpen(false)}
                  sx={{
                    width: "100%", py: "16px", bgcolor: "#3F5EFB", borderRadius: "6px",
                    cursor: "pointer", textAlign: "center",
                    fontSize: "16px", fontWeight: 500, fontFamily: "Instrument Sans, sans-serif", color: "#fff",
                    "&:hover": { opacity: 0.9 },
                  }}
                >
                  Fermer
                </Box>
              </Box>
            </Box>
          ) : (
            <>
              {/* Textarea */}
              <TextField
                fullWidth
                multiline
                rows={5}
                placeholder="Expliquez-nous votre question ou problématique…"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                sx={{
                  mb: "8px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    bgcolor: "#FAFAFA",
                    fontSize: "16px",
                    fontFamily: "Roboto, sans-serif",
                    "& fieldset": { borderColor: "#E1E1E2", borderWidth: "2px" },
                    "&:hover fieldset": { borderColor: "#bbb" },
                  },
                }}
              />
              <Typography sx={{ fontSize: "16px", color: "#999", fontFamily: "Roboto, sans-serif", mb: "24px" }}>
                100 caractères maximum*
              </Typography>

              {/* Actions */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "16px" }}>
                <Box
                  component="label"
                  sx={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", "&:hover": { opacity: 0.7 } }}
                >
                  <input type="file" hidden />
                  <FileArrowUp size={24} color="#3F5EFB" />
                  <Typography sx={{ fontSize: "16px", color: "#3F5EFB", fontFamily: "Roboto, sans-serif" }}>
                    Joindre un fichier
                  </Typography>
                </Box>
                <Box
                  onClick={handleSendEmail}
                  sx={{
                    px: "24px",
                    py: "10px",
                    bgcolor: emailSending || !emailMessage.trim() ? "#A0AFFD" : "#3F5EFB",
                    borderRadius: "6px",
                    cursor: emailSending || !emailMessage.trim() ? "default" : "pointer",
                    fontSize: "16px",
                    fontWeight: 500,
                    fontFamily: "Instrument Sans, sans-serif",
                    color: "#fff",
                    "&:hover": { opacity: emailSending || !emailMessage.trim() ? 1 : 0.9 },
                  }}
                >
                  {emailSending ? "Envoi…" : "Envoyer ma demande"}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Dialog>

      {/* Phone callback fullscreen */}
      <Dialog
        open={phoneOpen}
        onClose={() => setPhoneOpen(false)}
        fullScreen
        PaperProps={{ sx: { bgcolor: "#FAFAFA", overflowY: "auto" } }}
      >
        <Box sx={{ maxWidth: 960, mx: "auto", width: "100%", px: isMobile ? 2 : "60px", py: isMobile ? 3 : "40px", minHeight: "100vh" }}>
          {/* Top bar */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative", mb: "60px" }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1C1C1C", fontFamily: "Montserrat, sans-serif" }}>
              Rappel téléphonique
            </Typography>
            <Box
              onClick={() => setPhoneOpen(false)}
              sx={{ position: "absolute", right: 0, display: "flex", alignItems: "center", gap: 1, cursor: "pointer", "&:hover": { opacity: 0.7 } }}
            >
              <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#999", fontFamily: "Roboto, sans-serif" }}>Fermer</Typography>
              <X size={22} color="#999" />
            </Box>
          </Box>

          {phoneSent ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
              <Box sx={{
                bgcolor: "#fff", border: "1.5px solid #E1E1E2", borderRadius: "8px",
                width: 510, maxWidth: "100%", p: "40px 54px",
                display: "flex", flexDirection: "column", alignItems: "center", gap: "20px",
              }}>
                <Box component="img" src="/assets/success.png" alt="" sx={{ width: 115, height: 134, objectFit: "contain" }} />
                <Typography sx={{ fontSize: "26px", fontWeight: 600, fontFamily: "Roboto, sans-serif", textAlign: "center", lineHeight: 1.3 }}>
                  Merci, votre message a bien été{" "}
                  <Box component="span" sx={{ color: "#3F5EFB" }}>envoyé !</Box>
                </Typography>
                <Typography sx={{ fontSize: "16px", fontFamily: "Roboto, sans-serif", color: "#999", textAlign: "center", letterSpacing: "0.16px" }}>
                  Nous vous rappelons {phoneTime.toLowerCase()}.
                </Typography>
                <Box
                  onClick={() => setPhoneOpen(false)}
                  sx={{
                    width: "100%", py: "16px", bgcolor: "#3F5EFB", borderRadius: "6px",
                    cursor: "pointer", textAlign: "center",
                    fontSize: "16px", fontWeight: 500, fontFamily: "Instrument Sans, sans-serif", color: "#fff",
                    "&:hover": { opacity: 0.9 },
                  }}
                >
                  Fermer
                </Box>
              </Box>
            </Box>
          ) : (
            <>
              {/* Title section */}
              <Box sx={{ display: "flex", gap: "25px", alignItems: "flex-start", mb: "28px" }}>
                <Box sx={{ width: 4, minHeight: 61, borderRadius: "2px", bgcolor: "#3F5EFB", flexShrink: 0 }} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <Typography sx={{ fontSize: "24px", fontWeight: 700, fontFamily: "Roboto, sans-serif", color: "#1C1C1C" }}>
                    Quand souhaitez-vous être rappelé ?
                  </Typography>
                  <Typography sx={{ fontSize: "16px", fontWeight: 400, fontFamily: "Roboto, sans-serif", color: "#000" }}>
                    Choisissez quand vous souhaitez être rappelé.
                  </Typography>
                </Box>
              </Box>

              {/* Phone number */}
              <Typography sx={{ fontSize: "16px", fontWeight: 500, fontFamily: "Roboto, sans-serif", color: "#000", mb: "8px" }}>
                Numéro de téléphone
              </Typography>
              <TextField
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+33 6 76 45 24 76"
                sx={{
                  mb: "8px",
                  width: isMobile ? "100%" : 356,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: "16px",
                    fontFamily: "Roboto, sans-serif",
                    "& fieldset": { borderColor: "#E1E1E2", borderWidth: "1.5px" },
                    "&:hover fieldset": { borderColor: "#bbb" },
                  },
                }}
              />
              <Typography sx={{ fontSize: "16px", color: "#999", fontFamily: "Roboto, sans-serif", mb: "24px" }}>
                Durée de l'appel : 5 à 10 minutes • Sans engagement
              </Typography>

              {/* Time + note */}
              <Box sx={{ display: "flex", gap: "16px", flexDirection: isMobile ? "column" : "row", alignItems: "flex-start" }}>
                {/* Radio options */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
                  {["Maintenant", "Dans 1 heure", "Dans 4 heures", "Dans 24 heures"].map((option) => (
                    <Box
                      key={option}
                      onClick={() => setPhoneTime(option)}
                      sx={{
                        display: "flex", alignItems: "center", gap: "16px",
                        px: "16px", py: "14px",
                        border: "1.5px solid #E1E1E2",
                        borderRadius: "8px",
                        bgcolor: "#fff",
                        cursor: "pointer",
                        "&:hover": { bgcolor: "#FAFAFA" },
                      }}
                    >
                      <Box
                        sx={{
                          width: 22, height: 22, borderRadius: "50%",
                          border: phoneTime === option ? "none" : "1.5px solid #E1E1E2",
                          bgcolor: phoneTime === option ? "#3F5EFB" : "transparent",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}
                      >
                        {phoneTime === option && (
                          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#fff" }} />
                        )}
                      </Box>
                      <Typography sx={{ fontSize: "16px", fontFamily: "Roboto, sans-serif", color: phoneTime === option ? "#000" : "#999" }}>
                        {option}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Note textarea */}
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    placeholder="Quelque chose à rajouter ?"
                    value={phoneNote}
                    onChange={(e) => setPhoneNote(e.target.value)}
                    sx={{
                      mb: "8px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        bgcolor: "#fff",
                        fontSize: "16px",
                        fontFamily: "Roboto, sans-serif",
                        fontStyle: "italic",
                        "& fieldset": { borderColor: "#E1E1E2", borderWidth: "1.5px" },
                        "&:hover fieldset": { borderColor: "#bbb" },
                      },
                    }}
                  />
                  <Typography sx={{ fontSize: "16px", color: "#999", fontFamily: "Roboto, sans-serif", mb: "16px" }}>
                    100 caractères maximum*
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Box
                      onClick={handleSendPhone}
                      sx={{
                        px: "24px", py: "10px",
                        bgcolor: phoneSending || !phoneNumber.trim() ? "#A0AFFD" : "#3F5EFB",
                        borderRadius: "6px",
                        cursor: phoneSending || !phoneNumber.trim() ? "default" : "pointer",
                        fontSize: "16px", fontWeight: 500,
                        fontFamily: "Instrument Sans, sans-serif",
                        color: "#fff",
                        "&:hover": { opacity: phoneSending || !phoneNumber.trim() ? 1 : 0.9 },
                      }}
                    >
                      {phoneSending ? "Envoi…" : "Confirmer le rappel"}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Dialog>
    </Dialog>
  );
}
